import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Chip,
  Typography,
  useMediaQuery,
  alpha
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Save as SaveIcon,
  SaveAlt as SaveDraftIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Styled components
const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(4),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  
  [theme.breakpoints?.down('sm') || '(max-width: 600px)']: {
    flexDirection: 'column',
    '& > *': {
      width: '100%'
    }
  }
}));

const SaveStatusChip = styled(Chip)(({ theme, status }) => {
  const getColors = () => {
    switch (status) {
      case 'saving':
        return {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          color: theme.palette.warning.main,
          borderColor: theme.palette.warning.main
        };
      case 'saved':
        return {
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.main,
          borderColor: theme.palette.success.main
        };
      case 'error':
        return {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.main,
          borderColor: theme.palette.error.main
        };
      default:
        return {
          backgroundColor: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.main,
          borderColor: theme.palette.info.main
        };
    }
  };

  return {
    ...getColors(),
    border: `1px solid`,
    fontWeight: 500,
    fontSize: '0.75rem',
    
    '& .MuiChip-icon': {
      color: 'inherit'
    }
  };
});

/**
 * Save status indicator component
 */
const SaveStatus = ({ 
  saveStatus, 
  lastSaved, 
  hasUnsavedChanges,
  onSaveDraft 
}) => {
  const formatLastSaved = (timestamp) => {
    if (!timestamp) return null;
    
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) {
      return `Saved ${minutes}m ago`;
    } else if (seconds > 10) {
      return `Saved ${seconds}s ago`;
    } else {
      return 'Just saved';
    }
  };

  const getSaveStatusContent = () => {
    switch (saveStatus) {
      case 'saving':
        return {
          label: 'Saving...',
          icon: <CircularProgress size={16} />,
          status: 'saving'
        };
      case 'saved':
        return {
          label: formatLastSaved(lastSaved) || 'Saved',
          icon: <CheckIcon />,
          status: 'saved'
        };
      case 'error':
        return {
          label: 'Save failed',
          icon: <CloseIcon />,
          status: 'error'
        };
      default:
        if (hasUnsavedChanges) {
          return {
            label: 'Unsaved changes',
            icon: <SaveDraftIcon />,
            status: 'warning'
          };
        }
        return null;
    }
  };

  const statusContent = getSaveStatusContent();
  if (!statusContent) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SaveStatusChip
        icon={statusContent.icon}
        label={statusContent.label}
        size="small"
        variant="outlined"
        status={statusContent.status}
        clickable={statusContent.status === 'warning' && onSaveDraft}
        onClick={statusContent.status === 'warning' && onSaveDraft ? onSaveDraft : undefined}
      />
    </Box>
  );
};

/**
 * Navigation controls component for multi-step forms
 * 
 * This component provides navigation buttons, save status, and draft management
 * for multi-step forms with consistent behavior and styling.
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.isFirstStep - Whether this is the first step
 * @param {boolean} props.isLastStep - Whether this is the last step
 * @param {Function} props.onPrevious - Callback for previous button
 * @param {Function} props.onNext - Callback for next button
 * @param {Function} props.onSubmit - Callback for submit button
 * @param {Function} props.onSaveDraft - Callback for save draft
 * @param {Function} props.onCancel - Callback for cancel button
 * @param {string} props.cancelLink - Link for cancel button (if no onCancel)
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {boolean} props.isValidating - Whether step is validating
 * @param {boolean} props.canProceed - Whether user can proceed to next step
 * @param {string} props.nextButtonText - Text for next button
 * @param {string} props.previousButtonText - Text for previous button
 * @param {string} props.submitButtonText - Text for submit button
 * @param {string} props.saveDraftText - Text for save draft button
 * @param {string} props.cancelText - Text for cancel button
 * @param {React.ReactNode} props.submitIcon - Icon for submit button
 * @param {Object} props.saveStatus - Auto-save status information
 * @param {boolean} props.showSaveStatus - Whether to show save status
 * @param {boolean} props.showDraftButton - Whether to show save draft button
 * @param {boolean} props.linear - Whether navigation is linear
 * @param {Object} props.sx - Additional styles
 */
const NavigationControls = ({
  isFirstStep = false,
  isLastStep = false,
  onPrevious,
  onNext,
  onSubmit,
  onSaveDraft,
  onCancel,
  cancelLink,
  isSubmitting = false,
  isValidating = false,
  canProceed = true,
  nextButtonText = 'Next',
  previousButtonText = 'Back',
  submitButtonText = 'Submit',
  saveDraftText = 'Save Draft',
  cancelText = 'Cancel',
  submitIcon,
  saveStatus = {},
  showSaveStatus = true,
  showDraftButton = true,
  linear = true,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // Extract save status properties
  const {
    saveStatus: currentSaveStatus = 'idle',
    lastSaved,
    hasUnsavedChanges = false,
    isSaving = false
  } = saveStatus;
  
  /**
   * Renders the left side actions (back/cancel)
   */
  const renderLeftActions = () => {
    if (isFirstStep && (cancelLink || onCancel)) {
      // Show cancel on first step
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {cancelLink ? (
            <Button
              component={RouterLink}
              to={cancelLink}
              startIcon={<ChevronLeftIcon />}
              variant="outlined"
              color="primary"
            >
              {cancelText}
            </Button>
          ) : (
            <Button
              onClick={onCancel}
              startIcon={<ChevronLeftIcon />}
              variant="outlined"
              color="primary"
            >
              {cancelText}
            </Button>
          )}
        </Box>
      );
    } else if (!isFirstStep) {
      // Show back button
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={onPrevious}
            startIcon={<ChevronLeftIcon />}
            variant="outlined"
            color="primary"
            disabled={isSubmitting || isValidating}
          >
            {previousButtonText}
          </Button>
        </Box>
      );
    }
    
    return <Box />; // Empty space for alignment
  };
  
  /**
   * Renders the center status area
   */
  const renderCenterStatus = () => {
    if (!showSaveStatus && !showDraftButton) return null;
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        justifyContent: 'center',
        flex: 1,
        ...(isMobile && { order: 3, justifyContent: 'space-between' })
      }}>
        {showSaveStatus && (
          <SaveStatus
            saveStatus={currentSaveStatus}
            lastSaved={lastSaved}
            hasUnsavedChanges={hasUnsavedChanges}
            onSaveDraft={onSaveDraft}
          />
        )}
        
        {showDraftButton && onSaveDraft && !isSaving && (
          <Button
            onClick={onSaveDraft}
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<SaveDraftIcon />}
            disabled={isSubmitting || isValidating || !hasUnsavedChanges}
            sx={{ ml: 1 }}
          >
            {saveDraftText}
          </Button>
        )}
      </Box>
    );
  };
  
  /**
   * Renders the right side actions (next/submit)
   */
  const renderRightActions = () => {
    const isDisabled = isSubmitting || isValidating || (!canProceed && linear);
    
    if (isLastStep) {
      // Show submit button
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={onSubmit}
            variant="contained"
            color="primary"
            endIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                submitIcon || <CheckIcon />
              )
            }
            disabled={isDisabled}
            sx={{
              minWidth: 120,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : submitButtonText}
          </Button>
        </Box>
      );
    } else {
      // Show next button
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={onNext}
            variant="contained"
            color="primary"
            endIcon={
              isValidating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <ChevronRightIcon />
              )
            }
            disabled={isDisabled}
            sx={{
              minWidth: 100,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 10px rgba(123, 104, 238, 0.25)'
                : `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 16px rgba(123, 104, 238, 0.4)'
                  : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            {isValidating ? 'Validating...' : nextButtonText}
          </Button>
        </Box>
      );
    }
  };
  
  return (
    <NavigationContainer sx={sx} {...props}>
      {renderLeftActions()}
      {renderCenterStatus()}
      {renderRightActions()}
    </NavigationContainer>
  );
};

export default NavigationControls;